import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Why you should write [code] every day",
  "description": "",
  "published": "2018-12-03T00:00:00.000Z",
  "redirect_from": ["/blog/write-code-every-day/swizec/8795"],
  "categories": "Learning, Personal, Writing",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Yesterday, Corey asked me about the best advice I have to give.`}</p>
    <p>{`It was for a podcast episode of `}<a parentName="p" {...{
        "href": "https://thewritestuff.simplecast.fm/"
      }}>{`The Write Stuff`}</a>{`, his podcast about writing. Going to be a great episode. Gonna share when it's out 🎧`}</p>
    <p>{`But his question kept me up at night. It totally wasn't battling Gatsby and mdx. Nu-uh, not me.`}</p>
    <p>{`What `}<em parentName="p">{`is`}</em>{` the best advice you can give to an aspiring writer, programmer, engineer, artist, entrepreneur, `}<strong parentName="p">{`practitioner`}</strong>{` like you? A doer. Creator.`}</p>
    <p>{`And I believe it's this 👇`}</p>
    <ol>
      <li parentName="ol">{`Your taste is better than your skill`}</li>
      <li parentName="ol">{`Practice every day`}</li>
    </ol>
    <h2 {...{
      "id": "your-taste-is-better-than-your-skill"
    }}>{`Your taste is better than your skill`}</h2>
    <p><a parentName="p" {...{
        "href": "https://zenpencils.com/comic/90-ira-glass-advice-for-beginners/"
      }}>{`Ira Glass speaks of The Gap`}</a>{`. That place between taste and skill where growth happens.`}</p>
    <p>{`You see, you get into this game because you have good taste. You read good writing, and you like it. You use delightful software, and you love it. You see a beautiful website, and you think it's great. You see art, and you think, "Wow."`}</p>
    <p>{`That's your taste. That's what you're trying to achieve.`}</p>
    <p>{`And you write some stuff, build some code, make a website, draw some art. And you think it's shit.`}</p>
    <p>{`Your code doesn't work, your writing doesn't flow, your website looks like crap, and your art is kinda wonky.`}</p>
    <p>{`That's okay! That's your taste leaping ahead of your skill.`}</p>
    <p>{`To bridge the gap, you need a volume of work. And you need feedback. Feedback from yourself, from your peers, from everyone who will give it.`}</p>
    <p>{`Volume of work is the only way. Make a thing. Fix the thing. Improve your skill until it matches your taste. Soon enough you'll love the stuff you make.`}</p>
    <p>{`And with practice your taste will improve as well. You might never catch up and that's great!`}</p>
    <h2 {...{
      "id": "practice-every-day"
    }}>{`Practice every day`}</h2>
    <p>{`Quickest way to get that volume of work? Practice!`}</p>
    <p>{`If you practice once a year, I don't think that counts. You'll build some skill, but between sessions, you'll forget everything you learn. Plus, in the 60 adult years you've got, you'd make only 60 attempts.`}</p>
    <p>{`60 is not enough.`}</p>
    <p>{`If you practice once a month, that's better. 12 attempts per year, 180 in your adult life. Great for skills you aren't `}<em parentName="p">{`that`}</em>{` interested in or that take several days to complete. Like hiking. Or car repairs. Maybe launching tiny apps?`}</p>
    <p>{`A few hours of practice every month could do wonders. Each session is intense, so you learn a lot. And a month is short enough that you remember what you've learned and can build on it.`}</p>
    <p>{`But it's often hard to clear an entire day for stuff.`}</p>
    <p>{`That's where weekly practice shines. 3120 attempts in your adult life.`}</p>
    <p><em parentName="p">{`Two orders of magnitude`}</em>{` more practice than monthly practice. Calendars are weird like that.`}</p>
    <p>{`Just 1 hour of practice per week. Like sitting down for an hour every Monday morning to write a newsletter to 10,000 people. Or going for a run. Or writing some code with that new stuff you're learning.`}</p>
    <p>{`That's 3120 hours of practice in your adult life. A monthly practitioner would have to spend 5 hours each session to beat you. 5 hours is one hell of a commitment compared to just 1 hour 🧐`}</p>
    <p>{`And what's more: Weekly practice compounds more than monthly practice.`}</p>
    <p>{`Because you remember more from last time, because you're more in the groove, it's easier to get started, easier to keep going, and you build skills on top of each other faster.`}</p>
    <p>{`Which is where daily practice trumps all.`}</p>
    <p>{`Even skipping weekends you get 15,120 hours of practice in your adult life. With just an hour a day.`}</p>
    <p>{`That's crazy town. What would take a lifetime with weekly practice takes just 12 years when you practice daily.`}</p>
    <p>{`Yes, 12 years sounds like a lot. But compared to 60?`}</p>
    <p>{`Practicing every day compounds like crazy as well. Each day is fresh in your mind when you start the next. You know what went wrong, you feel what went right. You know exactly what to do next.`}</p>
    <p>{`You can even work on the same project over many days solving different parts of it because it's fresh in your mind.`}</p>
    <h2 {...{
      "id": "but-how-do-you-practice-every-day"
    }}>{`But how do you practice every day?`}</h2>
    <p>{`The best way is a job doing the thing you want to practice.`}</p>
    <p>{`Jobs are great at feeding you realistic new challenges to solve every day. Jobs are great at forcing you to practice every day. Jobs are great at crushing your fear of the empty page and forcing you to sit down and get it done.`}</p>
    <p>{`The hardest part of any creative endeavor is starting. That moment when you sit down and nothing happens for 5 minutes and you want to run away and hide in social media or Netflix or take a nap.`}</p>
    <p>{`A job won't let you do that. Gotta sit there and do the thing.`}</p>
    <p>{`Once you've done the thing. A new challenge appears.`}</p>
    <p>{`It's great 👌`}</p>
    <p>{`When you can't or don't want to get a job doing the thing, your best bet is to challenge yourself. Or to find a challenge group.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      